/**
 * Cocon Uploads Tracking — screen 11, built to **v2** of Ubi's spreadsheet.
 *
 * ⚠ **The mockup in `mockups/11-…` shows v1, a plain table.** V2 (16 Sep 2026) turned this
 * board into four tiles, and this screen is built to v2 — so for once the React screen is
 * ahead of the validated mockup rather than a port of it. The mockup has been refreshed to
 * match, but Ubi has not seen either; that is flagged in `mockups/README.md` and in
 * "Things waiting on a human".
 *
 * ⚠ **The item name IS the drawing name** — `AISPRE-1C-B003` is the same identifier as
 * `projectname` in DMP and on Drawing Control, and the POP column matches `area_pop`. That
 * is the link worth having: a drawing still `with HP Partner` here is one Ubi probably
 * should not have published to a contractor yet.
 *
 * Three things on this screen exist because the numbers would otherwise mislead:
 *
 *   1. **Tile 3 says how many items can be overdue at all.** Only 57 of 170 carry a rework
 *      due date, so "0 overdue" would mean "none due", not "all clear". Question 9.
 *   2. **Tile 4 has an Overdue column and a Later column.** 9 of the 26 planned uploads
 *      fall outside the six months v2 asked for — the earliest dated 28 Feb 2026, seven
 *      months late. A window starting at the current month is the one place that row can
 *      never appear. Question 7.
 *   3. **The no-company row is shown, not dropped.** 7 of 170 items have no company, so
 *      tile 1 and tile 2 would otherwise disagree by 7 with nothing on screen to say why.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from "react";

import {
  ACCEPTANCE_ORDER,
  acceptanceMatrix,
  filterUploads,
  formatDayMonthYear,
  formatNumber,
  plannedUploadBuckets,
  reworkOverdue,
  summariseUploads,
  type LabelColours,
  type UploadItem,
} from "@cocon-stats/shared";
import { AlertTriangle, ChevronDown, ChevronRight, Inbox, RefreshCw, Search, X } from "lucide-react";
import { toast } from "sonner";

import { SortableHead, TableFilterSummary, useTableView, type SortColumn } from "@/components/SortableTable";
import { Donut } from "@/components/charts/Donut";
import { HBars } from "@/components/charts/HBars";
import { MondayPill, mondayColour } from "@/components/MondayPill";
import { BoardHeader, Kpi, TileCard, TileHeader } from "@/pages/MondayRequests";
import { ApiError, apiFetch } from "@/lib/api-client";

/** The label `acceptanceMatrix` gives the row for items with no company. */
const NO_COMPANY = "No company";

interface UploadsResponse {
  meta: {
    boardId: string;
    boardName: string;
    fetchedAt: string;
    durationMs: number;
    internal: boolean;
    scopeLabels: string[] | null;
    boardTotal: number | null;
    /** ⚠ The server's day, sent with the answer. See below. */
    today: string;
  };
  colours: LabelColours;
  items: UploadItem[];
}

export function MondayUploads() {
  const [data, setData] = useState<UploadsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; retryable: boolean } | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const [company, setCompany] = useState<string | null>(null);
  const [acceptance, setAcceptance] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [openCell, setOpenCell] = useState<string | null>(null);
  const [showOverdue, setShowOverdue] = useState(true);
  const [showPlanned, setShowPlanned] = useState(true);

  const load = useCallback(async (force = false) => {
    try {
      setError(null);
      setData(await apiFetch<UploadsResponse>(`/reports/monday-uploads-tracking${force ? "?refresh=1" : ""}`));
    } catch (err) {
      const api = err instanceof ApiError ? err : null;
      setError({ message: api?.message ?? "The report could not be loaded.", retryable: api?.status === 503 });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const items = data?.items ?? [];
  const colours = data?.colours ?? {};
  /**
   * ⚠ The server's day, never `new Date()` here.
   *
   * Tiles 3 and 4 both pivot on "today". A client computing it for itself disagrees with
   * the server across midnight and across a time zone, so two people comparing screens
   * would see two different overdue counts for the same board — hard rule 26, and the exact
   * failure this product exists to prevent.
   */
  const today = data?.meta.today ?? "1970-01-01";

  const shown = useMemo(
    () =>
      filterUploads(items, {
        company: company === NO_COMPANY ? undefined : (company ?? undefined),
        companyUnset: company === NO_COMPANY,
        acceptance: acceptance ?? undefined,
        search,
      }),
    [items, company, acceptance, search],
  );
  const summary = useMemo(() => summariseUploads(shown, today), [shown, today]);
  const matrix = useMemo(() => acceptanceMatrix(shown), [shown]);
  const overdue = useMemo(() => reworkOverdue(shown, today), [shown, today]);
  const planned = useMemo(() => plannedUploadBuckets(shown, today), [shown, today]);
  const companies = useMemo(
    () => acceptanceMatrix(items).map((row) => ({ label: row.company, n: row.total })),
    [items],
  );

  /**
   * Tile 2's columns.
   *
   * ⚠ The "No company" row is forced LAST whatever the sort, by giving it a null sort key.
   * It is not a company — it is seven items nobody filled in — and floating the board's
   * data-entry gap to the top of a descending sort would put it above every real company.
   */
  const matrixColumns = useMemo<Array<SortColumn<(typeof matrix)[number]>>>(
    () => [
      {
        key: "company",
        label: "Company",
        width: 180,
        value: (row) => (row.company === NO_COMPANY ? null : row.company),
        filter: (row) => row.company,
      },
      ...ACCEPTANCE_ORDER.map((status) => ({
        key: status,
        label: status,
        align: "right" as const,
        value: (row: (typeof matrix)[number]) =>
          row.company === NO_COMPANY ? null : (row.byStatus[status]?.length ?? 0),
      })),
      {
        key: "total",
        label: "Total",
        align: "right" as const,
        value: (row: (typeof matrix)[number]) => (row.company === NO_COMPANY ? null : row.total),
      },
    ],
    [matrix],
  );
  const matrixView = useTableView(matrix, matrixColumns);

  if (loading) {
    return (
      <div className="px-4 py-6 lg:px-8">
        <div className="card flex items-center gap-3 p-6">
          <RefreshCw className="h-4 w-4 animate-spin text-slate-400" />
          <div>
            <p className="text-sm font-semibold text-slate-700">Reading the board from Monday…</p>
            {/* ⚠ Says how long and why. A full read of a board is six round trips and takes
                about fifteen seconds; a bare "Loading…" for that long reads as a hang, and
                the next thing somebody does is reload, which starts it again. */}
            <p className="mt-0.5 text-xs text-slate-500">
              170 upload requests — this takes a few seconds the first time. Everyone who opens it in the next few minutes gets
              the same read, instantly.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="px-4 py-6 lg:px-8">
        <div className="card p-6" style={{ borderLeft: "4px solid #dc2626" }}>
          <h3 className="font-semibold text-slate-800">Monday could not be read</h3>
          <p className="mt-2 max-w-xl text-sm text-slate-600">{error?.message}</p>
          <p className="mt-2 max-w-xl text-xs text-slate-500">
            This is a problem reading the board, not an empty board. Nothing has been changed on Monday.
          </p>
          <button
            type="button"
            className="btn btn-sm gradient-btn mt-4"
            onClick={() => {
              setLoading(true);
              void load(true);
            }}
          >
            <RefreshCw className="h-3.5 w-3.5" /> Try again
          </button>
        </div>
      </div>
    );
  }

  const accepted = summary.byAcceptance.find((a) => a.label === "Accepted")?.n ?? 0;

  return (
    <div className="max-w-[1600px] space-y-6 px-4 py-6 lg:px-8">
      <BoardHeader
        meta={data.meta}
        refreshing={refreshing}
        onRefresh={() => {
          setRefreshing(true);
          void apiFetch("/reports/monday/refresh", { method: "POST" }).catch(() => undefined);
          void load(true).then(() => toast.success("Board re-read from Monday."));
        }}
      />

      <section className="card flex flex-wrap items-center gap-2 p-4">
        <span className="eyebrow mr-1">Company</span>
        <button type="button" className={`chip${company === null ? " chip-active" : ""}`} onClick={() => setCompany(null)}>
          All
        </button>
        {companies.map((c) => (
          <button
            key={c.label}
            type="button"
            className={`chip${company === c.label ? " chip-active" : ""}`}
            // ⚠ "No company" is a real row on this board but not a real company, so
            // selecting it asks for the items whose company column is empty rather than
            // comparing a display string against the data.
            onClick={() => setCompany(company === c.label ? null : c.label)}
            title={
              c.label === NO_COMPANY
                ? `${c.n} items have no company set on the board — they are in no company's row`
                : undefined
            }
          >
            {c.label} <span className="num text-slate-400">{formatNumber(c.n)}</span>
          </button>
        ))}
        <div className="relative ml-auto w-full sm:w-64">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            <Search className="h-4 w-4" />
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search a drawing or POP"
            className="field field-icon"
          />
        </div>
      </section>

      <section className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <Kpi accent="rose" label="Upload requests" value={summary.total} note="On the board" />
        <Kpi
          accent="emerald"
          label="Accepted"
          value={accepted}
          note={`${summary.total ? Math.round((accepted / summary.total) * 100) : 0}% of these`}
          tone="#047857"
        />
        <Kpi accent="orange" label="Planned" value={summary.planned} note="Awaiting upload" />
        <Kpi
          accent="red"
          label="Rework overdue"
          value={summary.overdue}
          // ⚠ The denominator, on the card. "0 overdue" with no context reads as "all
          // clear", when only a third of the board can be overdue at all.
          note={`of ${formatNumber(summary.withReworkDue)} with a due date`}
          tone={summary.overdue > 0 ? "#b91c1c" : "#0f172a"}
        />
      </section>

      <section className="grid items-start gap-4 xl:grid-cols-2">
        {/* Tile 1 — acceptance status */}
        <TileCard title="Acceptance Status" tile="Tile 1">
          <Donut
            centreLabel="Requests"
            slices={ACCEPTANCE_ORDER.filter((label) => summary.byAcceptance.some((a) => a.label === label)).map(
              (label) => ({
                label,
                n: summary.byAcceptance.find((a) => a.label === label)?.n ?? 0,
                color: mondayColour(colours, "acceptance", label),
              }),
            )}
            onSelect={(label) => setAcceptance(acceptance === label ? null : label)}
            selected={acceptance}
          />
          <p className="mt-4 text-xs text-slate-400">
            In workflow order, not by size. Click a row to filter every tile below.
          </p>
        </TileCard>

        {/* The iteration bars are not one of v2's tiles, but the board carries the column and
            it is the one measure of whether drawings are getting better or worse. */}
        <TileCard title="Current Iteration" tile="Board column">
          <HBars
            rows={summary.byIteration.map((i) => ({
              label: i.label,
              n: i.n,
              color: mondayColour(colours, "iteration", i.label),
            }))}
          />
          <p className="mt-4 text-xs text-slate-400">
            How many rounds each drawing has taken. IT1 is a drawing accepted first time.
          </p>
        </TileCard>
      </section>

      {/* Tile 2 — company × acceptance status */}
      <section className="card overflow-hidden">
        <TileHeader title="Acceptance Status Per Company" tile="Tile 2" />
        <div className="custom-scrollbar overflow-x-auto">
          <table className="tbl">
            <SortableHead columns={matrixColumns} view={matrixView} />
            <tbody>
              {matrixView.rows.map((row) => (
                <Fragment key={row.company}>
                  <tr>
                    <td className="font-semibold text-slate-800">
                      {row.company}
                      {row.company === NO_COMPANY && (
                        <span className="pill pill-amber ml-2" title="These items have no company set on the board">
                          Unset
                        </span>
                      )}
                    </td>
                    {ACCEPTANCE_ORDER.map((status) => {
                      const cell = row.byStatus[status] ?? [];
                      const key = `${row.company}-${status}`;
                      return (
                        <td key={status} className="text-right">
                          {cell.length === 0 ? (
                            <span className="text-slate-200">·</span>
                          ) : (
                            // ⚠ The accordion lists the drawings, as v2 asks. A count
                            // alone cannot answer "which drawings", which is the only
                            // actionable question on this tile.
                            <button
                              type="button"
                              className="num font-semibold text-slate-700 hover:underline"
                              onClick={() => setOpenCell(openCell === key ? null : key)}
                              title={`${cell.length} drawing${cell.length === 1 ? "" : "s"} — click to list`}
                            >
                              {cell.length}
                            </button>
                          )}
                        </td>
                      );
                    })}
                    <td className="num text-right font-bold text-slate-800">{formatNumber(row.total)}</td>
                  </tr>
                  {ACCEPTANCE_ORDER.filter((status) => openCell === `${row.company}-${status}`).map((status) => (
                    <tr key={`${row.company}-${status}-open`}>
                      <td colSpan={ACCEPTANCE_ORDER.length + 2} className="bg-slate-50">
                        <p className="eyebrow mb-2">
                          {row.company} · {status}
                        </p>
                        <div
                          className="custom-scrollbar overflow-x-auto overflow-y-auto rounded border border-slate-200 bg-white"
                          style={{ maxHeight: 420 }}
                        >
                          <UploadDatesTable items={row.byStatus[status] ?? []} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
              {matrix.length === 0 && (
                <tr>
                  <td colSpan={ACCEPTANCE_ORDER.length + 2} className="py-14 text-center">
                    <Inbox className="mx-auto h-6 w-6 text-slate-300" />
                    <p className="mt-3 text-sm text-slate-500">Nothing matches these filters.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Tile 3 — rework overdue */}
      <section className="card overflow-hidden">
        <div className="gradient-band flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-white">
          <span className="text-[11px] font-bold uppercase tracking-wider">Rework Overdue</span>
          <span className="text-xs text-white/85">Tile 3</span>
        </div>

        <div className="flex items-start gap-2.5 px-5 py-3" style={{ background: "#fffbeb", borderBottom: "1px solid #fde68a" }}>
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "#b45309" }} />
          <p className="text-xs" style={{ color: "#b45309" }}>
            {/* ⚠ Question 9, on screen rather than in a document. */}
            Only <span className="font-bold">{formatNumber(summary.withReworkDue)}</span> of{" "}
            <span className="font-bold">{formatNumber(summary.total)}</span> requests carry a rework due date at all, so
            this tile can only ever see those. <span className="font-bold">Zero overdue means none due</span>, not all
            clear.
          </p>
        </div>

        <div className="p-5">
          {overdue.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500">
              Nothing is past its rework due date{summary.withReworkDue === 0 && " — and nothing on this board has one"}.
            </p>
          ) : (
            <>
              <div className="space-y-2">
                {Object.entries(
                  overdue.reduce<Record<string, UploadItem[]>>((acc, item) => {
                    const key = item.company ?? "No company";
                    (acc[key] ??= []).push(item);
                    return acc;
                  }, {}),
                )
                  .sort((a, b) => b[1].length - a[1].length)
                  .map(([name, list]) => (
                    <div key={name} className="flex items-center gap-3">
                      <span className="w-36 shrink-0 truncate text-xs text-slate-500">{name}</span>
                      <div className="h-5 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${(list.length / overdue.length) * 100}%`, background: "#dc2626" }}
                        />
                      </div>
                      <span className="num w-10 shrink-0 text-right text-sm font-bold text-slate-800">
                        {list.length}
                      </span>
                    </div>
                  ))}
              </div>

              <button
                type="button"
                className="btn btn-sm btn-ghost mt-4 w-full justify-between"
                onClick={() => setShowOverdue((s) => !s)}
              >
                <span>The {formatNumber(overdue.length)} overdue drawings</span>
                {showOverdue ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>

              {/* Both axes named — see the note on tile 4 of the Requests screen. */}
              {showOverdue && (
                <div
                  className="custom-scrollbar mt-2 overflow-x-auto overflow-y-auto border-t border-slate-100 pt-2"
                  style={{ maxHeight: 560 }}
                >
                  <OverdueTable items={overdue} today={today} colours={colours} />
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Tile 4 — planned uploads by month */}
      <section className="card overflow-hidden">
        <div className="gradient-band flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-white">
          <span className="text-[11px] font-bold uppercase tracking-wider">Planned Uploads</span>
          <span className="text-xs text-white/85">Tile 4</span>
        </div>

        <div className="flex items-start gap-2.5 px-5 py-3" style={{ background: "#fffbeb", borderBottom: "1px solid #fde68a" }}>
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "#b45309" }} />
          <p className="text-xs" style={{ color: "#b45309" }}>
            {/* ⚠ Question 7, and the reason is the most interesting row on the board. */}
            v2 asks for this month and the next five. <span className="font-bold">Overdue and Later columns are added</span>{" "}
            so the tile adds up to every planned upload — without them{" "}
            <span className="font-bold">
              {formatNumber(
                (planned.find((b) => b.key === "overdue")?.items.length ?? 0) +
                  (planned.find((b) => b.key === "later")?.items.length ?? 0),
              )}
            </span>{" "}
            of {formatNumber(summary.planned)} would be invisible, including uploads still planned months after their
            date. Question 7 in the spec.
          </p>
        </div>

        <div className="custom-scrollbar overflow-x-auto p-5">
          <div className="flex min-w-[720px] items-end gap-3">
            {planned.map((bucket) => {
              const max = planned.reduce((m, b) => Math.max(m, b.items.length), 0) || 1;
              const special = bucket.key === "overdue" || bucket.key === "later" || bucket.key === "no-date";
              return (
                <div key={bucket.key} className="flex flex-1 flex-col items-center gap-2">
                  <span className="num text-sm font-bold text-slate-800">{bucket.items.length}</span>
                  <div
                    className="w-full rounded-t"
                    style={{
                      height: `${Math.max((bucket.items.length / max) * 120, bucket.items.length ? 6 : 2)}px`,
                      // Overdue is red because it is a problem; Later is slate because it is
                      // merely outside the window.
                      background:
                        bucket.key === "overdue" ? "#dc2626" : special ? "#94a3b8" : mondayColour(colours, "acceptance", "Upload Planned"),
                    }}
                    title={bucket.items.map((i) => i.name).join(", ") || "none"}
                  />
                  <span className={`text-[11px] ${special ? "font-semibold text-slate-600" : "text-slate-500"}`}>
                    {bucket.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {summary.planned > 0 && (
          <div className="px-5 pb-5">
            <button
              type="button"
              className="btn btn-sm btn-ghost w-full justify-between"
              onClick={() => setShowPlanned((s) => !s)}
            >
              <span>The {formatNumber(summary.planned)} planned uploads</span>
              {showPlanned ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            {showPlanned && (
              <div
                className="custom-scrollbar mt-2 overflow-x-auto overflow-y-auto border-t border-slate-100 pt-2"
                style={{ maxHeight: 560 }}
              >
                <UploadDatesTable items={planned.flatMap((bucket) => bucket.items)} showCompany />
              </div>
            )}
          </div>
        )}

        <div className="border-t border-slate-100 px-5 py-3 text-sm text-slate-500">
          <span className="num font-semibold text-slate-700">{formatNumber(summary.planned)}</span> planned uploads,
          every one of them in a column above.
        </div>
      </section>

      <p className="text-xs text-slate-400">
        Source: Monday.com board <span className="mono">{data.meta.boardId}</span>, read-only — nothing is ever written
        back. The request name is the drawing name, the same identifier the Order Activation report and Drawing Control
        use.
      </p>
    </div>
  );
}

/**
 * The overdue drawings, searchable and sortable on every column.
 *
 * Hugues asked for "search and sort on DRAWING, POP, COMPANY, etc columns". Every column
 * sorts; the search matches the three text columns a reader would type into — the drawing,
 * its POP and the company — and says how many rows it is hiding rather than silently
 * shortening the table.
 *
 * ⚠ `daysLate` is computed from the SERVER's `today`, the same value tile 3 and tile 4 use.
 * A table recomputing "now" for itself would disagree with the count in the heading above it
 * the moment somebody left the page open across midnight.
 */
function OverdueTable({
  items,
  today,
  colours,
}: {
  items: UploadItem[];
  today: string;
  colours: LabelColours;
}) {
  const daysLate = useCallback(
    (item: UploadItem) =>
      item.reworkDueDate
        ? Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${item.reworkDueDate}T00:00:00Z`)) / 86400000)
        : null,
    [today],
  );

  const columns = useMemo<Array<SortColumn<UploadItem>>>(
    () => [
      { key: "name", label: "Drawing", value: (i) => i.name, filter: (i) => i.name },
      { key: "pop", label: "POP", value: (i) => i.pop, filter: (i) => i.pop },
      { key: "company", label: "Company", value: (i) => i.company, filter: (i) => i.company },
      { key: "acceptance", label: "Acceptance", value: (i) => i.acceptance, filter: (i) => i.acceptance },
      // ⚠ IT1…IT7 sort as text, which happens to be their sequence. It is not a coincidence
      // worth relying on silently: if the board ever reaches IT10 this becomes wrong, and
      // that is the day to give it a numeric accessor.
      { key: "iteration", label: "Iteration", value: (i) => i.iteration, filter: (i) => i.iteration },
      // ⚠ Short headings, the board's full titles on hover. Nine columns at full length need
      // 1 266 px and overflowed the card below a 1 600 px window, cutting off Days late —
      // the column this table is sorted by. Measured, not guessed.
      { key: "planned", label: "HP planned", title: "HP planned upload date", value: (i) => i.hpPlannedUploadDate },
      { key: "uploaded", label: "Uploaded", title: "Uploaded to Cocon", value: (i) => i.uploadedToCoconDate },
      { key: "due", label: "Rework due", value: (i) => i.reworkDueDate },
      { key: "late", label: "Days late", align: "right", value: (i) => daysLate(i) },
    ],
    [daysLate],
  );

  // Most overdue first, which is the order the tile is for.
  const view = useTableView(items, columns, { initialSort: { key: "late", direction: "desc" } });

  return (
    <>
      {/* ⚠ The single box that used to sit here is gone: every text column has its own,
          under its heading. One box searching three columns at once could not answer "S30's
          overdue drawings in HUY", which is the question this tile exists for. */}
      <div className="mb-2 flex flex-wrap items-center justify-end gap-2">
        <TableFilterSummary view={view} noun="overdue drawings" />
      </div>

      <table className="tbl">
        <SortableHead columns={columns} view={view} sticky />
        <tbody>
          {view.rows.map((item) => {
            const late = daysLate(item) ?? 0;
            return (
              <tr key={item.id}>
                <td className="mono font-semibold text-slate-800">{item.name}</td>
                <td className="mono text-slate-600">{item.pop ?? <span className="text-slate-300">—</span>}</td>
                <td className="text-slate-700">
                  {item.company ?? <span className="pill pill-amber">No company</span>}
                </td>
                <td>
                  <MondayPill colours={colours} group="acceptance" label={item.acceptance} />
                </td>
                <td>
                  <MondayPill colours={colours} group="iteration" label={item.iteration} />
                </td>
                <DateCell value={item.hpPlannedUploadDate} />
                <DateCell value={item.uploadedToCoconDate} />
                <DateCell value={item.reworkDueDate} />
                <td className="num text-right">
                  <span className={`pill pill-num pill-${late > 90 ? "red" : late > 30 ? "orange" : "amber"}`}>
                    {late}
                  </span>
                </td>
              </tr>
            );
          })}
          {view.rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="py-10 text-center text-sm text-slate-500">
                No overdue drawing matches these column filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
}

/**
 * The drawings behind a tile 2 cell, or tile 4's planned uploads, with their two upload dates.
 *
 * "HP planned upload" is the column the board titled "Date of FIRST HP Upload" until Ubi
 * renamed it; "Uploaded to Cocon" is the one she added beside it in Oct 2026. Both are read
 * by column id, so the rename changed a heading and nothing else.
 *
 * Planned date first by default: in tile 4 that puts the uploads still waiting months after
 * their date at the top, which is the row the tile exists for.
 */
function UploadDatesTable({ items, showCompany = false }: { items: UploadItem[]; showCompany?: boolean }) {
  const columns = useMemo<Array<SortColumn<UploadItem>>>(
    () => [
      { key: "name", label: "Drawing", value: (i) => i.name, filter: (i) => i.name },
      { key: "pop", label: "POP", value: (i) => i.pop, filter: (i) => i.pop },
      ...(showCompany
        ? [{ key: "company", label: "Company", value: (i: UploadItem) => i.company, filter: (i: UploadItem) => i.company }]
        : []),
      { key: "planned", label: "HP planned upload", value: (i) => i.hpPlannedUploadDate },
      { key: "uploaded", label: "Uploaded to Cocon", value: (i) => i.uploadedToCoconDate },
    ],
    [showCompany],
  );
  const view = useTableView(items, columns, { initialSort: { key: "planned", direction: "asc" } });

  return (
    <>
      {view.isFiltered && (
        <div className="mb-2 flex justify-end">
          <TableFilterSummary view={view} noun="drawings" />
        </div>
      )}
      <table className="tbl">
        <SortableHead columns={columns} view={view} sticky />
        <tbody>
          {view.rows.map((item) => (
            <tr key={item.id}>
              <td className="mono font-semibold text-slate-800">{item.name}</td>
              <td className="mono text-slate-600">{item.pop ?? <span className="text-slate-300">—</span>}</td>
              {showCompany && (
                <td className="text-slate-700">{item.company ?? <span className="pill pill-amber">No company</span>}</td>
              )}
              <DateCell value={item.hpPlannedUploadDate} />
              <DateCell value={item.uploadedToCoconDate} />
            </tr>
          ))}
          {view.rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="py-10 text-center text-sm text-slate-500">
                No drawing matches these column filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
}

/** A Monday date column: a day, no time, so formatted in UTC — or an em dash when unset. */
function DateCell({ value }: { value: string | null }) {
  return (
    <td className="whitespace-nowrap text-slate-600">
      {value ? formatDayMonthYear(new Date(`${value}T00:00:00Z`)) : <span className="text-slate-300">—</span>}
    </td>
  );
}
